import React from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Drawer,
  Dialog,
  Divider,
  alpha,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  MailOutline as MailIcon,
  Business as ClinicIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Palette as PaletteIcon,
  LocationOn as LocationIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  Verified as VerifiedIcon,
  LockOutlined as LockIcon,
  FileUpload as UploadIcon,
} from "@mui/icons-material";
import { FaClinicMedical } from "react-icons/fa";
import { FORM_STATUS_OPTIONS, GENDER_OPTIONS, CORPORATE_TYPE_OPTIONS, PROFILE_OPTIONS, WEEKDAYS, TIME_OPTIONS } from "./constants";
import { useClinicTheme } from "../../theme";
import {
  ActionButton,
  ChoiceChipGroup,
  DrawerHeader,
  DrawerFooter,
  FormTextField,
  FormSelect,
  ThemeColorPicker,
} from "../../components/atoms";
import { DotStepper, DrawerField, tileInputProps } from "../../components/molecules";
import { URL } from "../../store/utils";
import {
  CLINIC_LOGO_MAX_BYTES,
  CLINIC_LOGO_ICON_MAX_BYTES,
  CLINIC_LOGO_MAX_WIDTH,
  CLINIC_LOGO_MAX_HEIGHT,
  CLINIC_LOGO_ICON_MAX_DIM,
  formatMaxSize,
} from "../../constants/uploadLimits";
import { COLORS, TYPO } from "../../styles/uiConstants";

const CREATE_STEPS = [{ label: "Klinik bilgileri" }, { label: "E-posta doğrulama" }];

/* ── Section wrapper: başlık + InfoTile ızgarası ─────────────────── */
function Section({ icon, title, children, last = false }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        {icon &&
          React.cloneElement(icon, {
            sx: { fontSize: 18, color: COLORS.slate400, ...icon.props?.sx },
          })}
        <Typography
          sx={{ fontWeight: 800, fontSize: TYPO.sectionTitle, color: COLORS.slate700, letterSpacing: 0.2 }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={TILE_GRID_SX}>{children}</Box>
      {!last && <Divider sx={{ borderColor: COLORS.slate200, my: 0.5 }} />}
    </Box>
  );
}

const TILE_GRID_SX = { display: "grid", rowGap: 2.5, columnGap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } };
const INPUT_PROPS = tileInputProps(true);
const TIME_SELECT_OPTIONS = TIME_OPTIONS.map((t) => ({ value: t, label: t }));

export default function ClinicFormDrawer({
  open,
  onClose,
  form,
  setField,
  onSubmit,
  canSubmit,
  isCreate,
  isSendingCode,
  isVerifying,
  isUpdateLoading,
  token,
  step,
  verificationCode,
  setVerificationCode,
  codeError,
  resendCountdown,
  onVerifyAndCreate,
  onResendCode,
  onBackToForm,
}) {
  const { palette } = useClinicTheme();
  const loading = isSendingCode || isVerifying || isUpdateLoading;
  const [previewDialogSrc, setPreviewDialogSrc] = React.useState("");
  const [logoPreview, setLogoPreview] = React.useState("");
  const [logoError, setLogoError] = React.useState("");
  const [logoIconPreview, setLogoIconPreview] = React.useState("");
  const [logoIconError, setLogoIconError] = React.useState("");
  const logoInputRef = React.useRef(null);
  const logoIconInputRef = React.useRef(null);
  const rawPreviewSrc = logoPreview || form.logoUrl || "";
  const rawIconPreviewSrc = logoIconPreview || form.logoIconUrl || "";
  const normalizedPreviewSrc = rawPreviewSrc.replace(/^\/+assets\//, "/assets/");
  const normalizedIconPreviewSrc = rawIconPreviewSrc.replace(/^\/+assets\//, "/assets/");
  const baseUrl = String(URL.url || "").replace(/\/+$/, "");
  const previewSrc =
    normalizedPreviewSrc && normalizedPreviewSrc.startsWith("/assets/")
      ? `${baseUrl}${normalizedPreviewSrc}`
      : normalizedPreviewSrc;
  const iconPreviewSrc =
    normalizedIconPreviewSrc && normalizedIconPreviewSrc.startsWith("/assets/")
      ? `${baseUrl}${normalizedIconPreviewSrc}`
      : normalizedIconPreviewSrc;

  React.useEffect(() => {
    if (!open) return;
    if (!form.logoFile) setLogoPreview("");
    setLogoError("");
    if (!form.logoIconFile) setLogoIconPreview("");
    setLogoIconError("");
  }, [open, form.logoFile, form.logoUrl, form.logoIconFile, form.logoIconUrl]);

  const validateAndSetLogo = (file, maxW, maxH, maxBytes, setPreview, setError, fileKey, clearKey) => {
    if (!file) return;
    setError("");
    const allowed = ["image/png", "image/jpeg", "image/webp"];
    if (!allowed.includes(file.type)) { setError("Sadece PNG, JPEG veya WebP."); return; }
    if (file.size > maxBytes) { setError(`Boyut en fazla ${formatMaxSize(maxBytes)} olmalı.`); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const img = new Image();
      img.onload = () => {
        if (img.width > maxW || img.height > maxH) { setError(`Maks ${maxW}x${maxH} px. Yüklenen: ${img.width}x${img.height}`); return; }
        if (img.width < img.height) { setError(`Genislik >= yükseklik olmali.`); return; }
        setPreview(result);
        setField(fileKey, file);
        setField(clearKey, false);
      };
      img.onerror = () => setError("Görsel okunamadı.");
      img.src = result;
    };
    reader.readAsDataURL(file);
  };

  // Gün eklenince 09:00-18:00 varsayılanıyla sona eklenir, çıkarılınca silinir.
  const handleWorkingDaysChange = (nextDays) => {
    const schedule = form.workingSchedule || [];
    const added = nextDays.find((d) => !schedule.some((s) => s.day === d));
    if (added !== undefined) {
      setField("workingSchedule", [...schedule, { day: added, startTime: "09:00", endTime: "18:00" }]);
    } else {
      setField("workingSchedule", schedule.filter((s) => nextDays.includes(s.day)));
    }
  };

  const showVerificationStep = isCreate && step === 2;
  const showFormStep = !isCreate || step === 1;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 500, md: 540 },
          bgcolor: COLORS.white,
          boxShadow: "0 8px 32px 0 rgba(60,72,100,0.18)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DrawerHeader
        icon={<FaClinicMedical size={32} color="white" />}
        title={isCreate ? "Klinik Oluştur" : "Klinik Düzenle"}
        subtitle={
          isCreate
            ? showVerificationStep
              ? "E-posta doğrulamasını tamamlayın."
              : "Klinik ve yönetici bilgilerini girin."
            : "Klinik bilgilerini güncelleyin."
        }
        onClose={onClose}
      />

      {/* Adım göstergesi — yalnız oluşturma akışında (bilgiler → e-posta doğrulama) */}
      {isCreate && (
        <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: `1px solid ${COLORS.slate200}`, flexShrink: 0 }}>
          <DotStepper steps={CREATE_STEPS} activeStep={showVerificationStep ? 1 : 0} />
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 3 }}>

        {/* ═══════════════════════════════════════════════════════════════
            DOĞRULAMA KODU ADIMI
        ═══════════════════════════════════════════════════════════════ */}
        {showVerificationStep && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={onBackToForm}
              sx={{
                alignSelf: "flex-start",
                textTransform: "none",
                fontWeight: 700,
                color: COLORS.slate500,
                "&:hover": { bgcolor: alpha(COLORS.slate200, 0.5) },
              }}
            >
              Bilgilere Geri Dön
            </Button>

            <Box
              sx={{
                bgcolor: COLORS.white,
                borderRadius: 4,
                p: { xs: 3, sm: 4 },
                border: `1px solid ${COLORS.slate200}`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
              }}
            >
              {/* Animated icon */}
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${alpha(palette.primary, 0.12)}, ${alpha(palette.primary, 0.04)})`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: `2px solid ${alpha(palette.primary, 0.2)}`,
                }}
              >
                <MailIcon sx={{ fontSize: 34, color: palette.primary }} />
              </Box>

              <Box sx={{ textAlign: "center" }}>
                <Typography sx={{ fontWeight: 800, color: COLORS.slate800, mb: 0.5, fontSize: TYPO.title }}>
                  Doğrulama Kodu Gönderildi
                </Typography>
                <Typography sx={{ color: COLORS.slate500, lineHeight: 1.7, fontSize: TYPO.subtitle }}>
                  <strong style={{ color: palette.primary }}>{form.email}</strong> adresine
                  6 haneli kod gönderildi. Kodu girerek klinik oluşturmayı tamamlayın.
                </Typography>
              </Box>

              {/* Code input */}
              <Box
                sx={{
                  bgcolor: COLORS.slate50,
                  borderRadius: 3,
                  p: 2.5,
                  width: "100%",
                  border: `2px solid ${codeError ? COLORS.error : COLORS.slate200}`,
                  transition: "border-color 0.2s",
                }}
              >
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setVerificationCode(val);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); onVerifyAndCreate(); }
                  }}
                  autoFocus
                  placeholder="------"
                  style={{
                    width: "100%",
                    border: "none",
                    outline: "none",
                    textAlign: "center",
                    fontSize: 32,
                    fontWeight: 800,
                    letterSpacing: 12,
                    background: "transparent",
                    color: COLORS.slate800,
                    fontFamily: "'Poppins', monospace",
                  }}
                />
              </Box>
              {codeError && (
                <Typography variant="caption" sx={{ color: COLORS.error, fontWeight: 600, mt: -1 }}>
                  {codeError}
                </Typography>
              )}

              <ActionButton
                variant="confirm"
                size="medium"
                fullWidth
                palette={palette}
                onClick={onVerifyAndCreate}
                disabled={!verificationCode?.trim() || verificationCode.length < 6}
                loading={isVerifying}
                label="Doğrula ve Oluştur"
                {...(isVerifying ? {} : { startIcon: <VerifiedIcon /> })}
                sx={{ py: 1.5, borderRadius: 3, fontSize: "0.95rem" }}
              />

              <Divider sx={{ width: "100%", borderColor: COLORS.slate200 }} />

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LockIcon sx={{ fontSize: 14, color: COLORS.slate400 }} />
                <Typography variant="caption" sx={{ color: COLORS.slate400 }}>
                  Kod 2 dakika geçerlidir
                </Typography>
              </Box>

              <Button
                variant="text"
                onClick={onResendCode}
                disabled={resendCountdown > 0}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: TYPO.body,
                  color: resendCountdown > 0 ? COLORS.slate400 : palette.primary,
                }}
              >
                {resendCountdown > 0
                  ? `Tekrar gönder (${resendCountdown}s)`
                  : "Kodu tekrar gönder"}
              </Button>
            </Box>
          </Box>
        )}

        {/* ═══════════════════════════════════════════════════════════════
            FORM ADIMI
        ═══════════════════════════════════════════════════════════════ */}
        {showFormStep && (
          <Box
            component="form"
            onSubmit={onSubmit}
            sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
          >
            {/* ── Klinik Bilgileri ── */}
            <Section icon={<ClinicIcon />} title="Klinik Bilgileri">
              <DrawerField label="Klinik Adı *" fullWidth>
                <FormTextField
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  required
                  fullWidth
                  variant="standard"
                  InputProps={INPUT_PROPS}
                />
              </DrawerField>
              <DrawerField label="Klinik E-posta *" fullWidth>
                <FormTextField
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  required
                  fullWidth
                  type="email"
                  variant="standard"
                  InputProps={INPUT_PROPS}
                />
              </DrawerField>
            </Section>

            {/* ── Yönetici Bilgileri (Create) ── */}
            {isCreate && (
              <>
                <Section icon={<PersonIcon />} title="Yönetici Kişisel Bilgileri">
                  <DrawerField label="Ad *">
                    <FormTextField
                      value={form.clinicManagerName}
                      onChange={(e) => setField("clinicManagerName", e.target.value)}
                      required
                      fullWidth
                      variant="standard"
                      InputProps={INPUT_PROPS}
                    />
                  </DrawerField>
                  <DrawerField label="Soyad *">
                    <FormTextField
                      value={form.clinicManagerSurname}
                      onChange={(e) => setField("clinicManagerSurname", e.target.value)}
                      required
                      fullWidth
                      variant="standard"
                      InputProps={INPUT_PROPS}
                    />
                  </DrawerField>
                  <DrawerField label="TC Kimlik No *">
                    <FormTextField
                      value={form.clinicManagerTcNo}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "").slice(0, 11);
                        setField("clinicManagerTcNo", val);
                      }}
                      required
                      fullWidth
                      variant="standard"
                      InputProps={INPUT_PROPS}
                      inputProps={{ maxLength: 11, inputMode: "numeric" }}
                      helperText={form.clinicManagerTcNo && form.clinicManagerTcNo.length !== 11 ? "11 hane olmalı" : ""}
                      error={Boolean(form.clinicManagerTcNo && form.clinicManagerTcNo.length !== 11)}
                    />
                  </DrawerField>
                  <DrawerField label="Telefon *">
                    <FormTextField
                      value={form.clinicManagerTellNo}
                      onChange={(e) => setField("clinicManagerTellNo", e.target.value)}
                      required
                      fullWidth
                      variant="standard"
                      InputProps={INPUT_PROPS}
                      placeholder="05XX XXX XX XX"
                    />
                  </DrawerField>
                  <DrawerField label="Cinsiyet" fullWidth>
                    <FormSelect
                      value={form.clinicManagerGender}
                      onChange={(v) => setField("clinicManagerGender", v)}
                      options={GENDER_OPTIONS}
                      placeholder="Seçiniz"
                      variant="standard"
                      InputProps={INPUT_PROPS}
                    />
                  </DrawerField>
                </Section>

                <Section icon={<LockIcon />} title="Yönetici Şifre" last>
                  <DrawerField label="Şifre *">
                    <FormTextField
                      type="password"
                      value={form.clinicManagerPassword}
                      onChange={(e) => setField("clinicManagerPassword", e.target.value)}
                      required
                      fullWidth
                      variant="standard"
                      InputProps={INPUT_PROPS}
                      helperText={form.clinicManagerPassword && form.clinicManagerPassword.length < 8 ? "En az 8 karakter" : ""}
                      error={Boolean(form.clinicManagerPassword && form.clinicManagerPassword.length < 8)}
                    />
                  </DrawerField>
                  <DrawerField label="Şifre Tekrar *">
                    <FormTextField
                      type="password"
                      value={form.clinicManagerPasswordConfirm}
                      onChange={(e) => setField("clinicManagerPasswordConfirm", e.target.value)}
                      required
                      fullWidth
                      variant="standard"
                      InputProps={INPUT_PROPS}
                      helperText={
                        form.clinicManagerPasswordConfirm &&
                        form.clinicManagerPassword !== form.clinicManagerPasswordConfirm
                          ? "Şifreler eşleşmiyor"
                          : ""
                      }
                      error={Boolean(
                        form.clinicManagerPasswordConfirm &&
                        form.clinicManagerPassword !== form.clinicManagerPasswordConfirm
                      )}
                    />
                  </DrawerField>
                </Section>
              </>
            )}

            {/* ── Edit: Durum & Tip ── */}
            {!isCreate && (
              <>
                <Section icon={<ClinicIcon />} title="Durum ve Tip">
                  <DrawerField label="Durum">
                    <FormSelect
                      value={form.status}
                      onChange={(v) => setField("status", v)}
                      options={FORM_STATUS_OPTIONS}
                      placeholder=""
                      variant="standard"
                      InputProps={INPUT_PROPS}
                    />
                  </DrawerField>
                  <DrawerField label="Klinik Tipi">
                    <FormSelect
                      value={form.isCorporate ? "true" : "false"}
                      onChange={(v) => setField("isCorporate", v === "true")}
                      options={CORPORATE_TYPE_OPTIONS}
                      placeholder=""
                      variant="standard"
                      InputProps={INPUT_PROPS}
                    />
                  </DrawerField>

                  {/* Klinik türleri (çoklu) — modülleri, terimleri ve önerilen rol
                      şablonlarını belirler. Yalnız oluştururken; sonra Klinik detayı › Modüller. */}
                  {isCreate && (
                    <DrawerField label="Klinik Türü (birden fazla seçilebilir)" fullWidth>
                      <ChoiceChipGroup
                        ariaLabel="Klinik türü"
                        multiple
                        required
                        options={PROFILE_OPTIONS}
                        value={form.profiles}
                        onChange={(next) => setField("profiles", next)}
                        palette={palette}
                        sx={{ mt: 0.5 }}
                      />
                    </DrawerField>
                  )}

                  {/* Bireyselde vergi alanları görünür ama kapalı, "Girilemez" yazar */}
                  <DrawerField label={form.isCorporate && form.status === "active" ? "Vergi Numarası *" : "Vergi Numarası"}>
                    <FormTextField
                      value={form.isCorporate ? form.taxNumber : ""}
                      onChange={(e) => setField("taxNumber", e.target.value)}
                      inputProps={{ maxLength: 10 }}
                      required={form.isCorporate && form.status === "active"}
                      disabled={!form.isCorporate}
                      placeholder={form.isCorporate ? undefined : "Girilemez"}
                      fullWidth
                      variant="standard"
                      InputProps={INPUT_PROPS}
                    />
                  </DrawerField>
                  <DrawerField label={form.isCorporate && form.status === "active" ? "Vergi Dairesi *" : "Vergi Dairesi"}>
                    <FormTextField
                      value={form.isCorporate ? form.taxOffice : ""}
                      onChange={(e) => setField("taxOffice", e.target.value)}
                      required={form.isCorporate && form.status === "active"}
                      disabled={!form.isCorporate}
                      placeholder={form.isCorporate ? undefined : "Girilemez"}
                      fullWidth
                      variant="standard"
                      InputProps={INPUT_PROPS}
                    />
                  </DrawerField>
                </Section>

                {/* ── İletişim ── */}
                <Section icon={<MailIcon />} title="İletişim">
                  <DrawerField label="Telefon">
                    <FormTextField
                      value={form.phone}
                      onChange={(e) => setField("phone", e.target.value)}
                      fullWidth
                      variant="standard"
                      InputProps={INPUT_PROPS}
                    />
                  </DrawerField>
                  <DrawerField label="Website">
                    <FormTextField
                      value={form.website}
                      onChange={(e) => setField("website", e.target.value)}
                      fullWidth
                      variant="standard"
                      InputProps={INPUT_PROPS}
                    />
                  </DrawerField>
                </Section>

                {/* ── Logo ── */}
                <Section icon={<ImageIcon />} title="Logo">
                  {/* Ana Logo */}
                  <DrawerField label="Logo" fullWidth>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                      <input
                        ref={logoInputRef}
                        type="file"
                        hidden
                        accept="image/png,image/webp,image/jpeg"
                        onChange={(e) => validateAndSetLogo(e.target.files?.[0], CLINIC_LOGO_MAX_WIDTH, CLINIC_LOGO_MAX_HEIGHT, CLINIC_LOGO_MAX_BYTES, setLogoPreview, setLogoError, "logoFile", "clearLogo")}
                      />
                      <ActionButton
                        variant="add"
                        compact
                        palette={palette}
                        label="Logo Yükle"
                        startIcon={<UploadIcon sx={{ fontSize: 15 }} />}
                        onClick={() => logoInputRef.current?.click()}
                      />
                      {previewSrc ? (
                        <>
                          <Box
                            component="img"
                            src={previewSrc}
                            alt="Logo"
                            onClick={() => setPreviewDialogSrc(previewSrc)}
                            sx={{
                              width: 48, height: 48, borderRadius: 2, objectFit: "contain",
                              border: `1px solid ${COLORS.slate200}`, bgcolor: COLORS.white, p: 0.5,
                              cursor: "pointer", transition: "transform 0.15s",
                              "&:hover": { transform: "scale(1.1)" },
                            }}
                          />
                          <ActionButton
                            variant="delete"
                            compact
                            label="Kaldır"
                            onClick={() => { setLogoPreview(""); setLogoError(""); setField("logoFile", null); setField("logoUrl", ""); setField("clearLogo", true); }}
                          />
                        </>
                      ) : (
                        <Typography variant="caption" sx={{ color: COLORS.slate400 }}>{`${CLINIC_LOGO_MAX_WIDTH}x${CLINIC_LOGO_MAX_HEIGHT} px, max ${formatMaxSize(CLINIC_LOGO_MAX_BYTES)}`}</Typography>
                      )}
                    </Box>
                    {logoError && <Typography variant="caption" sx={{ color: COLORS.error, fontWeight: 600, display: "block", mt: 0.75 }}>{logoError}</Typography>}
                  </DrawerField>

                  {/* İkon */}
                  <DrawerField label="İkon" fullWidth>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                      <input
                        ref={logoIconInputRef}
                        type="file"
                        hidden
                        accept="image/png,image/webp,image/jpeg"
                        onChange={(e) => validateAndSetLogo(e.target.files?.[0], CLINIC_LOGO_ICON_MAX_DIM, CLINIC_LOGO_ICON_MAX_DIM, CLINIC_LOGO_ICON_MAX_BYTES, setLogoIconPreview, setLogoIconError, "logoIconFile", "clearLogoIcon")}
                      />
                      <ActionButton
                        variant="add"
                        compact
                        palette={palette}
                        label="İkon Yükle"
                        startIcon={<UploadIcon sx={{ fontSize: 15 }} />}
                        onClick={() => logoIconInputRef.current?.click()}
                      />
                      {iconPreviewSrc ? (
                        <>
                          <Box
                            component="img"
                            src={iconPreviewSrc}
                            alt="İkon"
                            onClick={() => setPreviewDialogSrc(iconPreviewSrc)}
                            sx={{
                              width: 36, height: 36, borderRadius: 2, objectFit: "contain",
                              border: `1px solid ${COLORS.slate200}`, bgcolor: COLORS.white, p: 0.5,
                              cursor: "pointer", transition: "transform 0.15s",
                              "&:hover": { transform: "scale(1.1)" },
                            }}
                          />
                          <ActionButton
                            variant="delete"
                            compact
                            label="Kaldır"
                            onClick={() => { setLogoIconPreview(""); setLogoIconError(""); setField("logoIconFile", null); setField("logoIconUrl", ""); setField("clearLogoIcon", true); }}
                          />
                        </>
                      ) : (
                        <Typography variant="caption" sx={{ color: COLORS.slate400 }}>{`${CLINIC_LOGO_ICON_MAX_DIM}x${CLINIC_LOGO_ICON_MAX_DIM} px, max ${formatMaxSize(CLINIC_LOGO_ICON_MAX_BYTES)}`}</Typography>
                      )}
                    </Box>
                    {logoIconError && <Typography variant="caption" sx={{ color: COLORS.error, fontWeight: 600, display: "block", mt: 0.75 }}>{logoIconError}</Typography>}
                  </DrawerField>
                </Section>

                {/* ── Çalışma Saatleri ── */}
                <Section icon={<ScheduleIcon />} title="Çalışma Saatleri">
                  <DrawerField label="Açık Günler" fullWidth>
                    <ChoiceChipGroup
                      multiple
                      ariaLabel="Açık günler"
                      options={WEEKDAYS}
                      value={(form.workingSchedule || []).map((s) => s.day)}
                      onChange={handleWorkingDaysChange}
                      palette={palette}
                      sx={{ mt: 0.5, gap: 0.75 }}
                    />
                  </DrawerField>
                  {WEEKDAYS.filter((day) => (form.workingSchedule || []).some((s) => s.day === day.value)).map((day) => {
                    const entry = (form.workingSchedule || []).find((s) => s.day === day.value);
                    return (
                      <DrawerField key={day.value} label={day.label} fullWidth>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <FormSelect
                            value={entry?.startTime || "09:00"}
                            onChange={(v) => { setField("workingSchedule", (form.workingSchedule || []).map((s) => s.day === day.value ? { ...s, startTime: v } : s)); }}
                            options={TIME_SELECT_OPTIONS}
                            placeholder=""
                            variant="standard"
                            InputProps={INPUT_PROPS}
                            inputProps={{ "aria-label": `${day.label} başlangıç` }}
                            sx={{ flex: 1 }}
                          />
                          <Typography variant="caption" sx={{ color: COLORS.slate400, fontWeight: 700 }}>-</Typography>
                          <FormSelect
                            value={entry?.endTime || "18:00"}
                            onChange={(v) => { setField("workingSchedule", (form.workingSchedule || []).map((s) => s.day === day.value ? { ...s, endTime: v } : s)); }}
                            options={TIME_SELECT_OPTIONS}
                            placeholder=""
                            variant="standard"
                            InputProps={INPUT_PROPS}
                            inputProps={{ "aria-label": `${day.label} bitiş` }}
                            sx={{ flex: 1 }}
                          />
                          <IconButton
                            size="small"
                            aria-label={`${day.label} gününü kaldır`}
                            onClick={() => setField("workingSchedule", (form.workingSchedule || []).filter((s) => s.day !== day.value))}
                            sx={{ color: COLORS.slate400, "&:hover": { color: COLORS.error } }}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </DrawerField>
                    );
                  })}
                </Section>

                {/* ── Görünüm ── */}
                <Section icon={<PaletteIcon />} title="Görünüm">
                  <DrawerField label="Tema Rengi" fullWidth>
                    <ThemeColorPicker value={form.themeColor} onChange={(hex) => setField("themeColor", hex)} />
                  </DrawerField>
                </Section>

                {/* ── Açıklama ── */}
                <Section icon={<DescriptionIcon />} title="Açıklama">
                  <DrawerField label="Açıklama" fullWidth>
                    <FormTextField
                      value={form.description}
                      onChange={(e) => setField("description", e.target.value)}
                      multiline
                      minRows={2}
                      fullWidth
                      variant="standard"
                      InputProps={INPUT_PROPS}
                    />
                  </DrawerField>
                </Section>

                {/* ── Adres ── */}
                <Section icon={<LocationIcon />} title="Adres" last>
                  <DrawerField label="Şehir">
                    <FormTextField
                      value={form.city}
                      onChange={(e) => setField("city", e.target.value)}
                      fullWidth
                      variant="standard"
                      InputProps={INPUT_PROPS}
                    />
                  </DrawerField>
                  <DrawerField label="İlçe">
                    <FormTextField
                      value={form.district}
                      onChange={(e) => setField("district", e.target.value)}
                      fullWidth
                      variant="standard"
                      InputProps={INPUT_PROPS}
                    />
                  </DrawerField>
                  <DrawerField label="Tam Adres" fullWidth>
                    <FormTextField
                      value={form.fullAddress}
                      onChange={(e) => setField("fullAddress", e.target.value)}
                      multiline
                      minRows={2}
                      fullWidth
                      variant="standard"
                      InputProps={INPUT_PROPS}
                    />
                  </DrawerField>
                </Section>
              </>
            )}
          </Box>
        )}
      </Box>

      {/* ── Footer ── */}
      {showFormStep && (
        <DrawerFooter
          onCancel={onClose}
          onSubmit={onSubmit}
          submitLabel={isCreate ? "Doğrulama Kodu Gönder" : "Klinik Güncelle"}
          loadingLabel={isCreate ? "Kod gönderiliyor..." : "Güncelleniyor..."}
          loading={loading}
          disabled={!canSubmit || !token}
          startIcon={isCreate ? <MailIcon /> : <EditIcon />}
          fullWidth
        />
      )}

      {/* ── Logo Önizleme ── */}
      <Dialog
        open={Boolean(previewDialogSrc)}
        onClose={() => setPreviewDialogSrc("")}
        maxWidth="sm"
        PaperProps={{ sx: { bgcolor: "transparent", boxShadow: "none", overflow: "visible" } }}
      >
        <Box sx={{ position: "relative" }}>
          <IconButton
            onClick={() => setPreviewDialogSrc("")}
            sx={{
              position: "absolute", top: -16, right: -16, bgcolor: "white",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)", "&:hover": { bgcolor: COLORS.slate100 }, zIndex: 1,
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          {previewDialogSrc && (
            <Box
              component="img"
              src={previewDialogSrc}
              alt="Logo Önizleme"
              sx={{ maxWidth: 400, maxHeight: 400, objectFit: "contain", borderRadius: 3, bgcolor: "white", p: 2 }}
            />
          )}
        </Box>
      </Dialog>
    </Drawer>
  );
}
